import { useState, useEffect } from 'react';
import { supabase, forceCheckAuthHealth } from '../lib/supabase';
import { getUserId } from '../lib/supabaseRest';
import { clearAllData } from '../db/database';
import myelinLogo from '../assets/myelin-logo-circle.png';
import './AuthStatus.css';

// Storage key used by Supabase client
const STORAGE_KEY = 'sb-urkfvgqpzeuixgdayrex-auth-token';

// Read user directly from localStorage (instant, no async)
export function getUserFromStorage(): any {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.user || null;
    }
  } catch (e) {
    console.error('[AuthStatus] Error reading from storage:', e);
  }
  return null;
}

interface AuthStatusProps {
  variant?: 'compact' | 'fullscreen';
}

export default function AuthStatus({ variant = 'compact' }: AuthStatusProps = {}) {
  // Initialize from localStorage immediately - this is instant
  const [user, setUser] = useState<any>(() => getUserFromStorage());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Also try getSession after a short delay to let Supabase initialize
    // This will update/confirm the user state from localStorage
    const timeoutId = setTimeout(() => {
      supabase.auth.getSession().then(({ data }) => {
        if (!cancelled) {
          // Only update if we got a session, or if localStorage is empty
          // This prevents clearing user on race conditions during init
          if (data.session?.user) {
            setUser(data.session.user);
          } else if (!getUserFromStorage()) {
            setUser(null);
          }
        }
      }).catch((e) => {
        console.error('[AuthStatus] Error getting session:', e);
      });
    }, 100);

    // Listen for auth changes - this handles login, logout, and token refresh
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!cancelled) {
        // For INITIAL_SESSION, validate stored session before keeping it
        // This prevents showing "logged in" with an expired/invalid token
        if (event === 'INITIAL_SESSION' && !session) {
          const storedUser = getUserFromStorage();
          if (storedUser) {
            // FIX: Validate that we can actually extract a user ID
            // If getUserId() returns null, the token is invalid/corrupted
            const userId = getUserId();
            if (userId) {
              console.log('[AuthStatus] INITIAL_SESSION with null but have valid stored user, keeping state');
              return; // Keep current state, don't clear
            } else {
              console.warn('[AuthStatus] INITIAL_SESSION: stored user exists but getUserId() failed - token invalid/expired');
              setUser(null); // Clear invalid state
              return;
            }
          }
        }

        // For explicit SIGNED_OUT, always clear
        if (event === 'SIGNED_OUT') {
          setUser(null);
          return;
        }

        // For SIGNED_IN, TOKEN_REFRESHED, or valid session, update
        if (session?.user) {
          setUser(session.user);
        }
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);

    try {
      if (authMode === 'signup') {
        // Validate password strength for new accounts
        if (password.length < 10) {
          setAuthError('Password must be at least 10 characters');
          return;
        }
        if (!/[A-Z]/.test(password)) {
          setAuthError('Password must contain at least one uppercase letter');
          return;
        }
        if (!/[0-9]/.test(password)) {
          setAuthError('Password must contain at least one number');
          return;
        }
        if (!/[^A-Za-z0-9]/.test(password)) {
          setAuthError('Password must contain at least one special character');
          return;
        }

        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;

        // SECURITY: Clear any stale IndexedDB data before setting user
        // This handles the race condition where logout didn't complete
        await clearAllData();
        console.log('[AuthStatus] ✅ Cleared stale data on signup');

        // FIX: Force auth health check to update status from 'checking' to 'healthy'
        // This ensures SyncEngine can proceed with initial sync
        await forceCheckAuthHealth();
        console.log('[AuthStatus] ✅ Auth health check complete');

        // Initialize sync engine after signup
        // This ensures sync starts immediately (though initial sync will be empty for new users)
        try {
          const { syncEngine } = await import('../sync/SyncEngine');
          await syncEngine.initialize();
          console.log('[AuthStatus] ✅ Sync engine initialized on signup');
        } catch (err) {
          console.error('[AuthStatus] Failed to initialize sync on signup:', err);
        }

        setUser(data.user);
        setShowAuthModal(false);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        // SECURITY: Clear any stale IndexedDB data before setting user
        // This handles the race condition where logout didn't complete
        // Without this, User B might see User A's data if logout was interrupted
        await clearAllData();
        console.log('[AuthStatus] ✅ Cleared stale data on signin');

        // FIX: Force auth health check to update status from 'checking' to 'healthy'
        // This ensures SyncEngine can proceed with initial sync
        await forceCheckAuthHealth();
        console.log('[AuthStatus] ✅ Auth health check complete');

        // Initialize sync engine after login
        // This ensures sync starts immediately and pulls data from Supabase
        try {
          const { syncEngine } = await import('../sync/SyncEngine');
          await syncEngine.initialize();
          console.log('[AuthStatus] ✅ Sync engine initialized on login');
        } catch (err) {
          console.error('[AuthStatus] Failed to initialize sync on login:', err);
        }

        setUser(data.user);
        setShowAuthModal(false);
      }
    } catch (err: any) {
      setAuthError(err.message);
    }
  };

  const handleLogout = async () => {
    console.log('[AuthStatus] 🔐 Starting secure logout sequence...');

    // Step 1: Stop sync engine FIRST to prevent new data from syncing during logout
    try {
      const { syncEngine } = await import('../sync/SyncEngine');
      syncEngine.destroy();
      console.log('[AuthStatus] ✅ Sync engine stopped');
    } catch (err) {
      console.warn('[AuthStatus] ⚠️ Failed to stop sync engine:', err);
      // Continue - sync engine might not be initialized
    }

    // Step 2: Sign out from Supabase (with timeout)
    const signOutPromise = supabase.auth.signOut();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('signOut timeout')), 3000)
    );

    try {
      await Promise.race([signOutPromise, timeoutPromise]);
      console.log('[AuthStatus] ✅ Supabase sign out complete');
    } catch (e) {
      console.warn('[AuthStatus] ⚠️ signOut timed out, clearing localStorage manually');
      // Manually clear auth storage if signOut hangs
      // IMPORTANT: Only clear auth-related keys, preserve theme and other settings
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('lastSyncAt');
    }

    // Step 3: Clear React state
    setUser(null);

    // Step 4: Clear IndexedDB - CRITICAL SECURITY: Wait for completion before redirect
    // IndexedDB is scoped to browser origin, NOT user, so data persists across logins
    // Without this, User B would see User A's data after logout
    try {
      await clearAllData();
      console.log('[AuthStatus] ✅ IndexedDB cleared on logout');
    } catch (err) {
      console.error('[AuthStatus] ❌ CRITICAL: Failed to clear IndexedDB on logout:', err);
      // Security-critical: Don't continue if clear fails
      alert('Failed to clear local data. Please clear your browser cache manually and try again.');
      return; // Don't redirect - force user to manually clear
    }

    // Step 5: Clear sync timestamp to force full sync on next login
    localStorage.removeItem('lastSyncAt');
    console.log('[AuthStatus] ✅ Logout sequence complete, redirecting...');

    // Step 6: Only NOW redirect (ensures all cleanup is done atomically)
    window.location.href = '/';
  };

  if (user) {
    return (
      <div className="auth-status">
        <div className="auth-user">
          <span className="user-email">{user.email}</span>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>
    );
  }

  const authModal = showAuthModal && (
    <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <img src={myelinLogo} alt="Myelin" className="auth-logo" style={{ width: '80px', height: '80px', margin: '0 auto 20px' }} />
        <h2>{authMode === 'signup' ? 'Sign Up' : 'Sign In'}</h2>

        <form onSubmit={handleAuth}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder={authMode === 'signup' ? "Min 10 chars, uppercase + number + special" : "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={authMode === 'signup' ? 10 : 1}
          />

          {authError && <div className="error-message">{authError}</div>}

          <button type="submit" className="auth-submit-btn">
            {authMode === 'signup' ? 'Sign Up' : 'Sign In'}
          </button>
        </form>

        <button
          className="toggle-mode-btn"
          onClick={() => setAuthMode(authMode === 'signup' ? 'signin' : 'signup')}
        >
          {authMode === 'signup' ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
        </button>

        <button className="close-modal-btn" onClick={() => setShowAuthModal(false)}>
          ✕
        </button>
      </div>
    </div>
  );

  if (variant === 'fullscreen') {
    return (
      <div className="auth-status auth-status-fullscreen">
        <img src={myelinLogo} alt="Myelin" className="auth-hero-logo" />
        <h1 className="auth-hero-title">Myelin</h1>
        <p className="auth-hero-tagline">Your second brain — capture, connect, and recall.</p>
        <button
          onClick={() => { setAuthMode('signin'); setShowAuthModal(true); }}
          className="auth-hero-btn auth-hero-btn-primary"
        >
          Sign In
        </button>
        <button
          onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
          className="auth-hero-btn auth-hero-btn-secondary"
        >
          Create Account
        </button>

        {authModal}
      </div>
    );
  }

  return (
    <div className="auth-status">
      <button onClick={() => setShowAuthModal(true)} className="login-btn">
        🔐 Login
      </button>
      {authModal}
    </div>
  );
}
